import { useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import UFOLabel from '@atlaskit/react-ufo/label';
import { token } from '@atlaskit/tokens';

import { Button } from '@/libraries/design/components/button/src/button';
import { wacToken } from '@/libraries/design/foundations';

export interface SourcetreeAppDisclaimerProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
}

const overlayStyle = css({
	position: 'fixed',
	inset: 0,
	backgroundColor: token('color.blanket', 'rgba(0, 0, 0, 0.75)'),
	overflow: 'hidden',
	textAlign: 'center',
	zIndex: 101,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	transition: 'visibility .2s ease-in-out, opacity .2s ease-in-out',
	visibility: 'visible',
	opacity: 1,
});

const modalStyle = css({
	backgroundColor: wacToken('color.neutral.light0'),
	boxSizing: 'border-box',
	width: 'auto',
	maxWidth: '450px',
	margin: '0 auto',
	borderRadius: '3px',
	boxShadow: token('elevation.shadow.overlay'),
	position: 'relative',
	textAlign: 'left',
	fontFamily:
		'Charlie Text, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif',
	color: wacToken('color.extended.blue1000'),
});

const closeButtonStyle = css({
	position: 'absolute',
	insetBlockStart: token('space.200'),
	insetInlineEnd: token('space.200'),
	border: 'none',
	background: 'transparent',
	cursor: 'pointer',
	fontSize: '20px',
	lineHeight: 1,
	fontWeight: 700,
	color: wacToken('color.neutral.dark900'),
});

const headingStyle = css({
	fontSize: '1.25rem',
	lineHeight: 1,
	fontWeight: 400,
	padding: '16px',
	paddingInlineEnd: '42px',
	margin: 0,
	borderBlockEnd: `1px solid ${wacToken('color.neutral.dark1100')}`,
	textAlign: 'left',
	color: wacToken('color.extended.blue1000'),
});

const descriptionContainerStyle = css({
	padding: '16px',
	textAlign: 'left',
});

const bodyTextStyle = css({
	fontSize: '1rem',
	lineHeight: 1.5,
	margin: 0,
	color: wacToken('color.extended.blue1000'),
});

const checkboxRowStyle = css({
	display: 'grid',
	gridTemplateColumns: 'min-content 1fr',
	gridGap: '8px',
	padding: '16px',
	paddingBlockStart: 0,
	textAlign: 'left',
	alignItems: 'flex-start',
});

const checkboxInputStyle = css({
	margin: 0,
	marginBlockStart: '6px',
	alignSelf: 'flex-start',
});

const checkboxTextWrapperStyle = css({
	marginBlockStart: '-2px',
	alignSelf: 'flex-start',
});

const checkboxTextStyle = css({
	fontSize: '1rem',
	lineHeight: 1.5,
	margin: 0,
	marginBlockEnd: '24px',
	color: 'inherit',
});

const linkStyle = css({
	color: wacToken('color.primary.blue'),
	textDecoration: 'none',
	':hover': {
		textDecoration: 'underline',
	},
});

const actionsStyle = css({
	borderBlockStart: `1px solid ${wacToken('color.neutral.dark1100')}`,
	padding: '16px',
	textAlign: 'right',
});

const cancelButtonStyle = css({
	fontSize: '1rem',
	lineHeight: 1.5,
	fontWeight: 400,
	color: wacToken('color.primary.blue'),
	background: 'none',
	border: 'none',
	padding: 0,
	marginInlineStart: '8px',
	cursor: 'pointer',
	textDecoration: 'none',
	':hover': {
		textDecoration: 'underline',
	},
});

const DOWNLOAD_URL =
	'https://product-downloads.atlassian.com/software/sourcetree/ga/Sourcetree_4.2.16_307.zip';
const POST_DOWNLOAD_URL = 'https://sourcetreeapp.com/post-download-content';

export const SourcetreeAppDisclaimer = ({
	isOpen,
	onClose,
	onConfirm,
}: SourcetreeAppDisclaimerProps) => {
	const intl = useIntl();
	const [isChecked, setIsChecked] = useState(false);

	if (!isOpen) {
		return null;
	}

	const title = intl.formatMessage({
		id: 'sourcetreeapp.disclaimer.title',
		defaultMessage: 'Important information',
	});

	const description = intl.formatMessage({
		id: 'sourcetreeapp.disclaimer.description',
		defaultMessage:
			'To continue downloading this product you must read and agree to the Atlassian Software License Agreement and Privacy Policy.',
	});

	const downloadLabel = intl.formatMessage({
		id: 'sourcetreeapp.disclaimer.download',
		defaultMessage: 'Download',
	});

	const cancelLabel = intl.formatMessage({
		id: 'sourcetreeapp.disclaimer.cancel',
		defaultMessage: 'Cancel',
	});

	const licenseText = intl.formatMessage({
		id: 'sourcetreeapp.disclaimer.software-license',
		defaultMessage: 'Atlassian Software License Agreement',
	});

	const privacyText = intl.formatMessage({
		id: 'sourcetreeapp.disclaimer.privacy-policy',
		defaultMessage: 'Privacy Policy',
	});

	const handleDownloadClick: React.MouseEventHandler = (event) => {
		event.preventDefault();
		if (!isChecked) {
			return;
		}
		onConfirm();

		// Open download in new tab
		window.open(DOWNLOAD_URL, '_blank');
		// Redirect current tab
		window.location.assign(POST_DOWNLOAD_URL);
	};

	return (
		<UFOLabel name="sourcetreeapp-disclaimer-modal">
			<div
				css={overlayStyle}
				role="dialog"
				aria-modal="true"
				aria-labelledby="sourcetreeapp-disclaimer-heading"
			>
				<div css={modalStyle}>
					<button
						type="button"
						css={closeButtonStyle}
						onClick={onClose}
						aria-label={intl.formatMessage({
							id: 'sourcetreeapp.disclaimer.close',
							defaultMessage: 'Close overlay',
						})}
					>
						×
					</button>

					<h2 css={headingStyle} id="sourcetreeapp-disclaimer-heading">
						{title}
					</h2>

					<div css={descriptionContainerStyle}>
						<p css={bodyTextStyle}>{description}</p>
					</div>
					<div css={checkboxRowStyle}>
						<label htmlFor="download-eula-checkbox" style={{ display: 'contents' }}>
							<input
								type="checkbox"
								id="download-eula-checkbox"
								name="download-eula-checkbox"
								checked={isChecked}
								onChange={(e) => setIsChecked(e.target.checked)}
								css={checkboxInputStyle}
							/>
							<div css={checkboxTextWrapperStyle}>
								<p css={checkboxTextStyle}>
									I agree to the{' '}
									<a
										href="https://www.atlassian.com/legal/software-license-agreement"
										css={linkStyle}
										target="_blank"
										rel="noopener noreferrer"
									>
										{licenseText}
									</a>{' '}
									and{' '}
									<a
										href="https://www.atlassian.com/legal/privacy-policy"
										css={linkStyle}
										target="_blank"
										rel="noopener noreferrer"
									>
										{privacyText}
									</a>
									.
								</p>
							</div>
						</label>
					</div>

					<div css={actionsStyle}>
						<Button
							variant="primary"
							size="small"
							type="button"
							onClick={handleDownloadClick}
							isDisabled={!isChecked}
						>
							{downloadLabel}
						</Button>
						<button type="button" css={cancelButtonStyle} onClick={onClose}>
							{cancelLabel}
						</button>
					</div>
				</div>
			</div>
		</UFOLabel>
	);
};
